'use client'

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Input from '../components/Input';
import Button from '../components/Button';
import Spinner from "../components/Spinner";
import Modal from '../components/Modal';
import Table from '../components/Table';
import { useToaster } from '../context/ToasterContext';
import Breadcrumbs from '../components/Breadcrumbs';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPolicies } from '../redux/globalDataSlice';
import KeywordForm from '../components/KeywordForm';
import api from '../api/axios';
import copyId from '../utils/copyId';
import { ClipboardDocumentCheckIcon, DocumentDuplicateIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

const schema = yup.object().shape({
  name: yup.string().required('Policy name is required'),
  keyWords: yup.array().of(
    yup.object().shape({
      id: yup.string(),
      language: yup.string().required('Please select a language'),
      countryCode: yup.string().required('Please select a country'),
      words: yup.string().required('Enter at least 1 word'),
      reply: yup.string().required('Keyword reply is required'),
      optOut: yup.boolean(),
      defaultKeyword: yup.boolean(),
    })
  )
  // Handling "at least one keyword" manually in onSubmit
});

const pages = [{ name: 'Opt-In & Opt-Out Policies', href: '/optin-optout-policies' }];

export default function AddEditPolicy() {
  const { id } = useParams();
  const isEditMode = !!id;

  const navigate = useNavigate();
  const { toaster } = useToaster();
  const dispatch = useDispatch();

  const activeAccountId = useSelector(s => s.globalData.activeAccountId);

  const [loading, setLoading] = useState(true);
  const [policyData, setPolicyData] = useState(null);

  const [showKeywordModal, setShowKeywordModal] = useState(false);
  const [currentKeyword, setCurrentKeyword] = useState(null);
  const [isOptOut, setIsOptOut] = useState(true);
  const [copied, setCopied] = useState(false);
  const [kwFilter, setKwFilter] = useState({ keywordType: '', countryCode: '', language: '' });

  const languageOptions = [
    { value: "en-US", label: "English (US)" },
    { value: "en-GB", label: "English (UK)" },
    { value: "fr-FR", label: "French" },
    { value: "de-DE", label: "German" },
    { value: "es-ES", label: "Spanish" },
    { value: "pt-PT", label: "Portuguese (Portugal)" },
    { value: "pt-BR", label: "Portuguese (Brazil)" },
    { value: "hi-IN", label: "Hindi" },
    { value: "en-AU", label: "English (Australia)" },
  ];

  const countryOptions = [
    { value: "US", label: "United States" },
    { value: "GB", label: "United Kingdom" },
    { value: "CA", label: "Canada" },
    { value: "DE", label: "Germany" },
    { value: "FR", label: "France" },
    { value: "ES", label: "Spain" },
    { value: "PT", label: "Portugal" },
    { value: "BR", label: "Brazil" },
    { value: "IN", label: "India" },
    { value: "AU", label: "Australia" },
    { value: "MX", label: "Mexico" },
  ];

  useEffect(() => {
    if (isEditMode) {
      fetchPolicyData();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch,
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      keyWords: [],
      accountId: ''
    },
  });

  // In create mode, seed accountId when we know it
  useEffect(() => {
    if (!isEditMode && activeAccountId) {
      setValue('accountId', activeAccountId, { shouldValidate: false, shouldDirty: false });
    }
  }, [isEditMode, activeAccountId, setValue]);

  const handleCopyId = async (val) => {
    const ok = await copyId(val);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    }
  };

  const generateTempId = () => {
    const timestamp = Date.now().toString();
    const randomDigits = Math.floor(100000 + Math.random() * 900000).toString();
    return `${timestamp}-${randomDigits}`;
  };

  const fetchPolicyData = async () => {
    try {
      const response = await api.get(
        `${process.env.REACT_APP_BASE_API_URL}/mvapi/utils/optout/account/${activeAccountId}/${id}`
      );
      setPolicyData(response.data);
    } catch (error) {
      console.error('Fetch policy error:', error);
      toaster.error('Failed to load policy: ' + error);
    } finally {
      setLoading(false);
    }
  };

  // When editing, populate the form and ensure accountId is present
  useEffect(() => {
    if (isEditMode && policyData) {
      const keywordsWithTempIds = (policyData.keyWords || []).map(keyword => ({
        ...keyword,
        temp_id: generateTempId()
      }));
      reset({
        ...policyData,
        accountId: policyData.accountId || activeAccountId || '',
        keyWords: keywordsWithTempIds
      });
    }
  }, [policyData, isEditMode, activeAccountId, reset]);

  const onSubmit = async (data) => {
    if ((data.keyWords || []).length === 0) {
      toaster.error("Please add at least 1 keyword!");
      return;
    }

    const accountId = data.accountId || activeAccountId;
    if (!accountId) {
      toaster.error('No account selected. Please choose an account first.');
      return;
    }

    try {
      const requestData = {
        ...data,
        accountId,
        keyWords: data.keyWords.map(({ temp_id, ...rest }) => rest),
        ...(isEditMode ? { id } : {})
      };

      if (isEditMode) {
        await api.put(`${process.env.REACT_APP_BASE_API_URL}/mvapi/utils/optout/account/${activeAccountId}/${id}`, requestData);
        toaster.success('Policy updated successfully!');
      } else {
        await api.post(`${process.env.REACT_APP_BASE_API_URL}/mvapi/utils/optout/account/${activeAccountId}/`, requestData);
        toaster.success('Policy created successfully!');
      }

      await dispatch(fetchPolicies(accountId)); // refresh for this account only
      navigate('/optin-optout-policies');
    } catch (error) {
      console.error('Save error:', error);
      toaster.error('Something went wrong. Please try again.');
      // Important: do NOT navigate on failure
    }
  };

  const openAddKeywordModal = (optOut) => {
    setIsOptOut(optOut);
    setCurrentKeyword(null);
    setShowKeywordModal(true);
  };

  const openEditKeywordModal = (keyword) => {
    setIsOptOut(!!keyword.optOut);
    setCurrentKeyword(keyword);
    setShowKeywordModal(true);
  };

  const handleSaveKeyword = (keywordData) => {
    let currentKeywords = watch('keyWords') || [];

    // Enforce single default per keyword type (opt-in or opt-out)
    if (keywordData.defaultKeyword) {
      currentKeywords = currentKeywords.map(kw => {
        const isSameType = !!kw.optOut === !!keywordData.optOut;
        const isBeingEdited = currentKeyword && kw.temp_id === currentKeyword.temp_id;
        return isSameType && !isBeingEdited ? { ...kw, defaultKeyword: false } : kw;
      });
    }

    if (currentKeyword) {
      const updatedKeywords = currentKeywords.map(kw =>
        kw.temp_id === currentKeyword.temp_id
          ? { ...keywordData, id: currentKeyword.id, temp_id: currentKeyword.temp_id }
          : kw
      );
      setValue('keyWords', updatedKeywords, { shouldValidate: true });
    } else {
      setValue('keyWords', [...currentKeywords, { ...keywordData, temp_id: generateTempId() }], {
        shouldValidate: true
      });
    }

    setShowKeywordModal(false);
  };

  const deleteKeyword = (temp_id) => {
    const currentKeywords = watch('keyWords') || [];
    setValue('keyWords', currentKeywords.filter(kw => kw.temp_id !== temp_id), {
      shouldValidate: true
    });
  };

  const waitingForAccount = !isEditMode && !activeAccountId;

  return (
    <>
      {(loading || waitingForAccount) && <Spinner />}

      {!loading && !waitingForAccount && (
        <div className="px-4 sm:px-6 lg:px-8">
          <Breadcrumbs pages={pages} />

          <h2 className="text-2xl/7 font-bold text-gray-900 dark:text-gray-100 sm:truncate sm:text-3xl sm:tracking-tight">
            {isEditMode ? 'Edit policy' : 'Create a policy'}
          </h2>

          {isEditMode && (policyData?.id || id) && (
            <div className="mt-2 text-sm text-gray-600 dark:text-gray-300 flex items-center gap-2">
              <span className="font-medium">Policy ID:</span>
              <code className="px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 font-mono break-all">
                {policyData?.id || id}
              </code>
              <button
                type="button"
                onClick={() => handleCopyId(policyData?.id || id)}
                className="inline-flex items-center rounded p-1 hover:bg-gray-100 dark:hover:bg-gray-800 transition"
                aria-label="Copy Policy ID"
                title={copied ? 'Copied!' : 'Copy Policy ID'}
              >
                {copied ? (
                  <ClipboardDocumentCheckIcon className="h-4 w-4" />
                ) : (
                  <DocumentDuplicateIcon className="h-4 w-4" />
                )}
              </button>
            </div>
          )}

          {/* hidden accountId to submit */}
          <input type="hidden" {...register('accountId')} />

          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="mt-8 bg-white dark:bg-black rounded-xl border border-gray-200 dark:border-gray-800 p-6">
              <div className="space-y-12">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-8">
                  <div className="col-span-2">
                    <Input
                      label="Policy name"
                      placeholder="e.g. Alerts"
                      required
                      {...register('name')}
                      error={errors.name?.message}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* keywords area start */}
            <div className="mt-6 bg-white dark:bg-black rounded-xl border border-gray-200 dark:border-gray-800">
              <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-800">
                <div className="-ml-4 -mt-4 flex flex-wrap items-center justify-between sm:flex-nowrap">
                  <div className="ml-4 mt-4">
                    <div className="flex items-center">
                      <h3 className="text-md font-semibold text-gray-900 dark:text-gray-100">Keywords</h3>
                    </div>
                  </div>
                  <div className="ml-4 mt-4 flex shrink-0">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => openAddKeywordModal(true)}
                      className="relative inline-flex items-center"
                    >
                      Create Opt-Out
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => openAddKeywordModal(false)}
                      className="relative inline-flex items-center ml-3"
                    >
                      Create Opt-In
                    </Button>
                  </div>
                </div>
              </div>
              {/* Keyword filter bar */}
              <div className="px-6 pt-4 flex flex-wrap items-center gap-3">
                <select
                  value={kwFilter.keywordType}
                  onChange={e => setKwFilter(f => ({ ...f, keywordType: e.target.value }))}
                  className="rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 px-3 py-1.5 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="">All types</option>
                  <option value="opt-out">Opt-Out</option>
                  <option value="opt-in">Opt-In</option>
                </select>
                <select
                  value={kwFilter.countryCode}
                  onChange={e => setKwFilter(f => ({ ...f, countryCode: e.target.value }))}
                  className="rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 px-3 py-1.5 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="">All countries</option>
                  {countryOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <select
                  value={kwFilter.language}
                  onChange={e => setKwFilter(f => ({ ...f, language: e.target.value }))}
                  className="rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 px-3 py-1.5 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="">All languages</option>
                  {languageOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                {(kwFilter.keywordType || kwFilter.countryCode || kwFilter.language) && (
                  <button
                    type="button"
                    onClick={() => setKwFilter({ keywordType: '', countryCode: '', language: '' })}
                    className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 underline"
                  >
                    Clear filters
                  </button>
                )}
              </div>
              <div className="space-y-6 p-6">
                <Table
                  keyField="temp_id"
                  data={(() => {
                    let kws = watch('keyWords') || [];
                    if (kwFilter.keywordType !== '') {
                      const isOptOutFilter = kwFilter.keywordType === 'opt-out';
                      kws = kws.filter(kw => !!kw.optOut === isOptOutFilter);
                    }
                    if (kwFilter.countryCode) kws = kws.filter(kw => kw.countryCode === kwFilter.countryCode);
                    if (kwFilter.language) kws = kws.filter(kw => kw.language === kwFilter.language);
                    return kws;
                  })()}
                  columns={[
                    {
                      key: 'optOut',
                      title: 'Keyword type',
                      render: (keyword) =>
                        keyword.optOut === true ? (
                          <span className="inline-flex items-center rounded-md bg-blue-100 px-2 py-1 text-xs font-medium text-blue-700 dark:bg-blue-900 dark:text-blue-300">
                            Opt-Out
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-md bg-blue-100 px-2 py-1 text-xs font-medium text-blue-700 dark:bg-blue-900 dark:text-blue-300">
                            Opt-In
                          </span>
                        )
                    },
                    { key: 'words', title: 'Words' },
                    { key: 'reply', title: 'Reply' },
                    { key: 'countryCode', title: 'Country code' },
                    { key: 'language', title: 'Language' },
                    {
                      key: 'defaultKeyword',
                      title: 'Default',
                      render: (keyword) =>
                        keyword.defaultKeyword === true ? (
                          <span className="inline-flex items-center rounded-md bg-yellow-100 px-2 py-1 text-xs font-medium text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                            Default
                          </span>
                        ) : null
                    },
                    {
                      key: 'actions',
                      title: 'Actions',
                      align: 'right',
                      render: (keyword) => (
                        <div className="space-x-2">
                          <button type="button" onClick={() => openEditKeywordModal(keyword)} className="text-emerald-600 dark:text-emerald-400 hover:text-emerald-900 dark:hover:text-emerald-100 mr-5">Edit</button>
                          <button type="button" onClick={() => deleteKeyword(keyword.temp_id)} className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-100">Delete</button>
                        </div>
                      )
                    }
                  ]}
                  emptyState={{
                    title: 'No keywords found',
                    description: 'Get started by creating a new Opt-In / Opt-Out keyword.'
                  }}
                />
              </div>
            </div>
            {/* keywords area end */}

            <div className="mt-6 flex items-center justify-end gap-x-6">
              <Button
                  type="button"
                  variant="secondary"
                  onClick={() => navigate('/optin-optout-policies')}
                  disabled={isSubmitting}
                >
                  Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? (isEditMode ? 'Updating...' : 'Saving...')
                  : (isEditMode ? 'Update details' : 'Save details')}
              </Button>
            </div>
          </form>
        </div>
      )}

      <Modal
        open={showKeywordModal}
        title={currentKeyword ? 'Edit keyword' : `Add ${isOptOut ? 'Opt-Out' : 'Opt-In'} keyword`}
        ctaButtonText={currentKeyword ? 'Update' : 'Add'}
        cancelButtonText="Cancel"
        onConfirm={() => document.getElementById('keyword-form').requestSubmit()}
        onCancel={() => setShowKeywordModal(false)}
      >
        <KeywordForm
          keyword={currentKeyword}
          isOptOut={isOptOut}
          languageOptions={languageOptions}
          countryOptions={countryOptions}
          onSubmit={handleSaveKeyword}
        />
      </Modal>
    </>
  );
}
